import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("sp-chat-session") ||
      (() => {
        const id = crypto.randomUUID();
        localStorage.setItem("sp-chat-session", id);
        return id;
      })()
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const openChat = () => {
    setOpen(true);
    setMessages((m) => (m.length ? m : [{ role: "assistant", text: t.chat.welcome }]));
  };

  const patchLast = (text) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", text };
      return copy;
    });

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, lang }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) {
            acc += payload.delta;
            patchLast(acc);
          } else if (payload.error) {
            acc = t.chat.error;
            patchLast(acc);
          }
        }
      }
      if (!acc) patchLast(t.chat.error);
    } catch {
      patchLast(t.chat.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={openChat}
          aria-label={t.chat.open}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 rounded-full bg-[#0E1B2A] py-3.5 pl-4 pr-5 text-sm font-semibold text-white shadow-[0_15px_40px_-10px_rgba(14,27,42,0.5)] transition-all duration-300 hover:bg-[#C87A57] hover:-translate-y-0.5"
        >
          <MessageCircle size={20} />
          <span className="hidden sm:inline">{t.chat.open}</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-50 flex h-[520px] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-[#0E1B2A]/10 bg-white shadow-[0_30px_70px_-15px_rgba(14,27,42,0.4)]"
        >
          <div className="flex items-center justify-between bg-[#0E1B2A] px-5 py-4">
            <div>
              <p className="font-serif text-lg font-semibold text-[#FAF8F5]">{t.chat.title}</p>
              <p className="flex items-center gap-1.5 text-xs text-[#FAF8F5]/60">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {t.chat.subtitle}
              </p>
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} aria-label="Fermer" className="rounded-full p-1.5 text-[#FAF8F5]/70 transition-colors hover:bg-white/10 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto bg-[#FAF8F5] px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-sm bg-[#C87A57] text-white"
                      : "rounded-bl-sm border border-[#0E1B2A]/8 bg-white text-[#0E1B2A] shadow-sm"
                  }`}
                >
                  {m.text || (busy && i === messages.length - 1 ? <span className="inline-flex gap-1 py-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#94A3B8]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#94A3B8] [animation-delay:0.15s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#94A3B8] [animation-delay:0.3s]" /></span> : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-[#0E1B2A]/8 bg-white p-3">
            <div className="flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.chat.placeholder}
                className="flex-1 rounded-full border border-[#0E1B2A]/12 bg-[#FAF8F5] px-4 py-2.5 text-sm text-[#0E1B2A] placeholder:text-[#94A3B8] focus:border-[#C87A57]"
              />
              <button
                data-testid="chat-send-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                aria-label={t.chat.send || "Envoyer"}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0E1B2A] text-white transition-colors hover:bg-[#C87A57] disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
